import React from 'react';
import { useTranslation } from 'react-i18next';

import useIsMobile from '@/hooks/useIsMobile.ts';

import * as S from './style.ts';

type AdvertisementSection = {
	title: string;
	frequency: { label: string; value: string };
	publishDate: { label: string; value: string };
	adFee: { label: string };
	deadline: { label: string; value: string };
	contact: { label: string; value: string };
	submit: { label: string; lines: string[] };
	documents: { label: string; description: string };
};

const PublicationAdvertisementGuide: React.FC = () => {
	const { t } = useTranslation();
	const isMobile = useIsMobile(900);
	const priceHeaders = t('publication.advertisement.priceTable.headers', { returnObjects: true }) as string[];
	const colorPrices = t('publication.advertisement.priceTable.colorPrices', { returnObjects: true }) as string[];
	const blackPrices = t('publication.advertisement.priceTable.blackPrices', { returnObjects: true }) as string[];
	const documentHeaders = t('publication.advertisement.documentsTable.headers', { returnObjects: true }) as string[];
	const sections = [
		t('publication.advertisement.journal', { returnObjects: true }) as AdvertisementSection,
		t('publication.advertisement.proceedings', { returnObjects: true }) as AdvertisementSection,
	];

	const renderLines = (lines: string[]) =>
		lines.map((line, index) => (
			<React.Fragment key={`${line}-${index}`}>
				{line}
				{index < lines.length - 1 ? <br /> : null}
			</React.Fragment>
		));

	const renderPriceTable = (className: string) => (
		<S.TablePanel>
		<S.Table className={className}>
			<S.TableHeadCell className="item1 center" style={{ gridArea: 'item1' }}>
				{priceHeaders[0]}
			</S.TableHeadCell>
			<S.TableHeadCell className="center" style={{ gridArea: 'item3' }}>
				{priceHeaders[1]}
			</S.TableHeadCell>
			<S.TableHeadCell className="center" style={{ gridArea: 'item5' }}>
				{priceHeaders[2]}
			</S.TableHeadCell>
			<S.TableHeadCell className="center" style={{ gridArea: 'item6' }}>
				{priceHeaders[3]}
			</S.TableHeadCell>

			{!isMobile && (
				<S.TableCell style={{ gridArea: 'item7' }} className="min-height point center item2">
					{t('publication.advertisement.priceTable.fullPage')}
				</S.TableCell>
			)}

			<S.TableCell style={{ gridArea: 'item8' }} className={`min-height point center ${isMobile ? 'head' : ''}`}>
				{t('publication.advertisement.priceTable.color')}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item10' }} className="min-height center">
				{colorPrices[0]}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item12' }} className="min-height center">
				{colorPrices[1]}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item13' }} className="min-height center">
				{colorPrices[2]}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item14' }} className={`min-height point center ${isMobile ? 'head' : ''}`}>
				{t('publication.advertisement.priceTable.blackWhite')}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item16' }} className="min-height center">
				{blackPrices[0]}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item18' }} className="min-height center">
				{blackPrices[1]}
			</S.TableCell>
			<S.TableCell style={{ gridArea: 'item19' }} className="min-height center">
				{blackPrices[2]}
			</S.TableCell>
		</S.Table>
		</S.TablePanel>
	);

	return (
		<S.Container>
			{sections.map((section, index) => (
				<S.ContentBox key={section.title}>
					<S.ContentTitle>{section.title}</S.ContentTitle>
					<S.ContentListLayout>
						<S.ContentTitleSub>{section.frequency.label}</S.ContentTitleSub>
						<S.ContentDescription>{section.frequency.value}</S.ContentDescription>
					</S.ContentListLayout>
					<S.ContentListLayout>
						<S.ContentTitleSub>{section.publishDate.label}</S.ContentTitleSub>
						<S.ContentDescription>{section.publishDate.value}</S.ContentDescription>
					</S.ContentListLayout>
					<S.ContentListLayout className="flex-col">
						<S.ContentTitleSub>{section.adFee.label}</S.ContentTitleSub>
						{renderPriceTable(index === 0 ? 'table1' : 'table1 proceeding')}
					</S.ContentListLayout>
					<S.ContentListLayout className="flex-col">
						<S.ContentTitleSub>{section.deadline.label}</S.ContentTitleSub>
						<S.ContentDescription>{section.deadline.value}</S.ContentDescription>
					</S.ContentListLayout>
					<S.ContentListLayout>
						<S.ContentTitleSub>{section.contact.label}</S.ContentTitleSub>
						<S.ContentDescription>{section.contact.value}</S.ContentDescription>
					</S.ContentListLayout>
					<S.ContentListLayout>
						<S.ContentTitleSub>{section.submit.label}</S.ContentTitleSub>
						<S.ContentDescription>{renderLines(section.submit.lines)}</S.ContentDescription>
					</S.ContentListLayout>
					<S.ContentListLayout className="flex-col">
						<S.ContentTitleSub>{section.documents.label}</S.ContentTitleSub>
						<S.ContentDescription>{section.documents.description}</S.ContentDescription>
						<S.TablePanel>
						<S.Table className="table3">
							{documentHeaders.map((header) => (
								<S.TableHeadCell key={header} className="center">
									{renderLines(header.split('\n'))}
								</S.TableHeadCell>
							))}

							{documentHeaders.map((header) => (
								<S.TableCell key={`${header}-cell`} className="min-height" />
							))}
						</S.Table>
						</S.TablePanel>
					</S.ContentListLayout>
				</S.ContentBox>
			))}
		</S.Container>
	);
};

export default PublicationAdvertisementGuide;
